const ONBOARDING_VERSION = "v1";
const ONBOARDING_KEY = `onboard:${ONBOARDING_VERSION}`;
const DISMISSED_KEY = `onboard:dismissed:${ONBOARDING_VERSION}`;
const SEEN_KEY = `onboard:seen:${ONBOARDING_VERSION}`;
const FIRST_RECORDING_KEY = `onboard:firstRecording:${ONBOARDING_VERSION}`;
const POST_STEP_SEEN_KEY = `onboard:postStepSeen:${ONBOARDING_VERSION}`;
export const DEFAULT_TEMPLATE_KEY = "onboard:defaultTemplate";

// The onboarding gates are remembered in this browser, like the other
// workspace preferences, and per account: someone else signing in on the
// same browser still sees their own welcome. Storage can be unavailable
// (private mode); the gates then last for this page load only.
type Account = number | string | null | undefined;
const read = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};
const write = (key: string): void => {
  try {
    window.localStorage.setItem(key, "true");
  } catch {
    // A convenience only.
  }
};
const forAccount = (key: string, account: Account): string =>
  account === null || account === undefined || account === ""
    ? key
    : `${key}:${account}`;

// Finished onboarding. The key without an account is the one written before
// gates were per account; it still counts, so nobody sees the welcome again.
export const isOnboarded = (account?: Account): boolean =>
  read(ONBOARDING_KEY) || read(forAccount(ONBOARDING_KEY, account));

export const markOnboarded = (account?: Account): void => {
  write(forAccount(ONBOARDING_KEY, account));
};

// Closed the welcome without finishing (close button, Escape, outside
// click). It stays closed on later page loads (staging E2E 2026-10-20, B4).
export const isOnboardingDismissed = (account?: Account): boolean =>
  read(forAccount(DISMISSED_KEY, account));

export const dismissOnboarding = (account?: Account): void => {
  write(forAccount(DISMISSED_KEY, account));
};

// Shown at all. The welcome is shown once per account: it is marked seen as
// soon as it opens, so a reload, another tab or a later visit does not show
// it again even if it was never closed (staging E2E 2026-10-21; B4 only
// remembered closing and finishing it).
export const isWelcomeSeen = (account?: Account): boolean =>
  read(forAccount(SEEN_KEY, account));

export const markWelcomeSeen = (account?: Account): void => {
  write(forAccount(SEEN_KEY, account));
};

export const shouldShowOnboarding = (account: Account): boolean =>
  account !== null &&
  account !== undefined &&
  !isOnboarded(account) &&
  !isOnboardingDismissed(account) &&
  !isWelcomeSeen(account);

export const isFirstRecordingDone = (): boolean => read(FIRST_RECORDING_KEY);

export const markFirstRecordingDone = (): void => {
  write(FIRST_RECORDING_KEY);
};

export const isPostStepSeen = (): boolean => read(POST_STEP_SEEN_KEY);

export const markPostStepSeen = (): void => {
  write(POST_STEP_SEEN_KEY);
};

// First run of a brand-new account (the welcome was shown and the account had
// no visits). Until its first visit is recorded, and for the rest of the page
// load in which it was, "What's new" does not open by itself: for a new
// account everything is new, and the first visit comes first. Accounts that
// never had a first run here (everyone before this, or a known account on a
// new browser) are not affected.
const FIRST_RUN_KEY = `onboard:firstRun:${ONBOARDING_VERSION}`;
type FirstRunState = "pending" | "done";
// Without storage the state lasts for this page load.
const firstRunMemory = new Map<string, FirstRunState>();
const firstRunDoneThisLoad = new Set<string>();

const readFirstRun = (key: string): FirstRunState | null => {
  try {
    const value = window.localStorage.getItem(key);
    if (value === "pending" || value === "done") return value;
  } catch {
    // Fall back to this page load.
  }
  return firstRunMemory.get(key) ?? null;
};
const writeFirstRun = (key: string, value: FirstRunState): void => {
  firstRunMemory.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A convenience only.
  }
};

export const startFirstRun = (account: Account): void => {
  if (account === null || account === undefined || account === "") return;
  const key = forAccount(FIRST_RUN_KEY, account);
  if (readFirstRun(key) === null) writeFirstRun(key, "pending");
};

export const finishFirstRun = (account: Account): void => {
  if (account === null || account === undefined || account === "") return;
  const key = forAccount(FIRST_RUN_KEY, account);
  if (readFirstRun(key) !== "pending") return;
  writeFirstRun(key, "done");
  firstRunDoneThisLoad.add(key);
};

export const isFirstRunPending = (account: Account): boolean => {
  if (account === null || account === undefined || account === "") return false;
  const key = forAccount(FIRST_RUN_KEY, account);
  return firstRunDoneThisLoad.has(key) || readFirstRun(key) === "pending";
};

// Tests only: forget what this page load remembered.
export const resetFirstRunMemory = (): void => {
  firstRunMemory.clear();
  firstRunDoneThisLoad.clear();
};
